import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, FileDown, Loader2, XCircle, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { waLink } from "../lib/data";

const Shell = ({ children, testid }) => (
  <main data-testid={testid} className="relative min-h-screen pt-[120px] pb-24 grid place-items-center px-5">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_50%_30%,rgba(255,210,0,0.12),transparent)]" />
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
      className="relative max-w-xl w-full text-center rounded-[28px] border border-white/10 bg-[#111219] p-8 sm:p-12">
      {children}
    </motion.div>
  </main>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const pagarmeRef = params.get("provider") === "pagarme" ? params.get("ref") : null;
  const [state, setState] = useState("checking");
  const [contractLink, setContractLink] = useState(null);

  useEffect(() => {
    if (!sessionId && !pagarmeRef) { setState("error"); return undefined; }
    const url = pagarmeRef ? `/payments/pagarme/status/${pagarmeRef}` : `/payments/status/${sessionId}`;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(url);
        if (data?.payment_status === "paid") {
          if (data?.contract_id && data?.contract_token) {
            setContractLink(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${data.contract_id}/pdf?token=${encodeURIComponent(data.contract_token)}`);
          }
          setState("paid");
          return;
        }
        if (data?.status === "expired") { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries >= 15) setState("timeout");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, pagarmeRef]);

  return (
    <Shell testid="payment-success-modal">
      <img src="/brand/claw.png?v=2" alt="" className="w-24 mx-auto" />
      {state === "checking" && (
        <div data-testid="payment-status-checking">
          <Loader2 className="w-8 h-8 mx-auto mt-6 animate-spin text-[#FFD200]" />
          <p className="mt-4 text-zinc-300">Confirmando seu pagamento…</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-status-paid">
          <CheckCircle2 className="w-10 h-10 mx-auto mt-6 text-[#FFD200]" />
          <h1 className="mt-4 font-display font-black uppercase text-3xl sm:text-4xl text-white">Data garantida!</h1>
          <p className="mt-4 text-zinc-300">Pagamento confirmado. Em breve entraremos em contato pelo WhatsApp para alinhar os detalhes do seu evento.</p>
          {contractLink && (
            <a href={contractLink} data-testid="download-contract-pdf" className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#FFD200] text-[#FFD200] px-6 py-3 font-semibold hover:bg-[#FFD200] hover:text-[#08080C] transition-colors">
              <FileDown className="w-4 h-4" /> Baixar contrato (PDF)
            </a>
          )}
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div data-testid="payment-status-pending">
          <h1 className="mt-6 font-display font-black uppercase text-2xl text-white">Ainda não confirmamos</h1>
          <p className="mt-4 text-zinc-300">Não conseguimos confirmar o pagamento agora. Se o valor foi cobrado, fale com a gente que verificamos na hora.</p>
        </div>
      )}
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/" data-testid="payment-back-home" className="rounded-full bg-[#FFD200] text-[#08080C] font-semibold px-6 py-3 hover:bg-white transition-colors">Voltar ao site</Link>
        <a href={waLink("Olá! Acabei de fazer uma reserva pelo site.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-white hover:border-[#FFD200]">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
      </div>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell testid="payment-cancel-page">
    <XCircle className="w-10 h-10 mx-auto text-[#E8202A]" />
    <h1 className="mt-4 font-display font-black uppercase text-3xl text-white">Pagamento cancelado</h1>
    <p className="mt-4 text-zinc-300">Nenhuma cobrança foi feita. Você pode tentar novamente quando quiser.</p>
    <Link to="/reservar" data-testid="payment-retry" className="mt-8 inline-block rounded-full bg-[#FFD200] text-[#08080C] font-semibold px-6 py-3 hover:bg-white transition-colors">Tentar novamente</Link>
  </Shell>
);
